import ContinuingEducationRequest from "models/continuing-education";
import { CONTINUING_EDUCATION_STATUS } from "models/continuing-education-status";
import EmployeeUtility from "utils/models/employee-utility";
import { formatCurrency, formatDateTimeAmPm } from "utils/tools";
import logoHorizontal from "assets/images/logo-horizontal.png";

// A request needs a Secondary Approver only when the cost is over the remaining budget.
export const needsSecondaryApproval = (cost: number, remainingBudget: number): boolean =>
    cost > remainingBudget;

export enum ContinuingEducationApprovalStage {
    MANAGER = 'manager',
    SECONDARY = 'secondary',
    APPROVED = 'approved',
}

// The stage a request is currently at.
export const getApprovalStage = (
    request: ContinuingEducationRequest
): ContinuingEducationApprovalStage => {
    if (!request.managerDecidedAt) return ContinuingEducationApprovalStage.MANAGER;
    if (request.secondaryApproverId && !request.secondaryDecidedAt) return ContinuingEducationApprovalStage.SECONDARY;
    return ContinuingEducationApprovalStage.APPROVED;
};

// The employee allowed to act at `stage`.
export const getStageApproverId = (
    request: ContinuingEducationRequest,
    stage: ContinuingEducationApprovalStage
): string | null | undefined =>
    stage === ContinuingEducationApprovalStage.MANAGER ? request.managerApproverId : request.secondaryApproverId;

export const isDeclined = (request: ContinuingEducationRequest): boolean =>
    request.status?.description === CONTINUING_EDUCATION_STATUS.DECLINED;

// Only the requester can edit, and only while the request is Pending or Declined.
export const canEdit = (request: ContinuingEducationRequest, employeeId?: string): boolean =>
    !!employeeId && request.employeeId === employeeId
    && (request.status?.description === CONTINUING_EDUCATION_STATUS.PENDING || isDeclined(request));

const toDate = (value?: Date | string | null) => value ? value.toString().split("T")[0] : "-"

// Values for public/templates/continuing-education-request.html.
export const getPrintValues = (request: ContinuingEducationRequest): Record<string, string> => ({
    logoUrl: new URL(logoHorizontal, window.location.origin).href,
    courseName: request.courseName,
    type: request.type?.description ?? "-",
    topic: request.topic?.description ?? "-",
    environment: request.environment?.description ?? "-",
    status: request.status?.description ?? "-",
    employeeName: EmployeeUtility.fullName(request.employee, "Not found"),
    submittedAt: formatDateTimeAmPm(request.submittedAt),
    managerName: EmployeeUtility.fullName(request.managerApprover, "Not found"),
    managerDecidedAt: formatDateTimeAmPm(request.managerDecidedAt),
    secondaryApproverName: request.secondaryApproverId ? EmployeeUtility.fullName(request.secondaryApprover, "Not found") : "Not needed",
    secondaryDecidedAt: request.secondaryApproverId ? formatDateTimeAmPm(request.secondaryDecidedAt) : "-",
    cost: formatCurrency(request.cost),
    remainingBudget: formatCurrency(request.remainingBudget),
    courseHours: request.courseHours.toString(),
    courseStartDate: toDate(request.courseStartDate),
    courseEndDate: toDate(request.courseEndDate),
    courseLink: request.courseLink || "-",
    courseDescription: request.courseDescription,
    managerComment: request.managerComment || "-",
    secondaryComment: request.secondaryComment || "-",
    generatedAt: formatDateTimeAmPm(new Date()),
})
